import { useState } from 'react';
import {
  GameState,
  GameTemplate,
  GameHistory,
  EditDialog,
  EditTeamDialog,
  PendingRetirement,
  PendingMaxBallsOver,
  WicketDetails
} from '../types/game';
import { createInitialGameState } from '../constants/game';
import {
  checkInningsComplete,
  checkLastManStands,
  startSecondInnings,
  determineWinner,
  getNextBatterName,
  getNextBowlerName,
  calculateTarget
} from '../utils/gameLogic';
import { handleScoringAction, handleWicketAction } from '../utils/scoringLogic';

interface SavedTeam {
  id: string;
  name: string;
  players: string[];
  createdAt: Date;
}

export function useGameLogic() {
  const [gameState, setGameState] = useState<GameState>(createInitialGameState());
  const [gameHistory, setGameHistory] = useState<GameState[]>([createInitialGameState()]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [gameTemplates, setGameTemplates] = useState<GameTemplate[]>([]);
  const [gameHistoryRecords, setGameHistoryRecords] = useState<GameHistory[]>([]);
  const [savedTeams, setSavedTeams] = useState<SavedTeam[]>(() => {
    const saved = localStorage.getItem('cricketSavedTeams');
    if (saved) {
      try {
        const parsedTeams = JSON.parse(saved);
        // Convert date strings back to Date objects
        return parsedTeams.map((team: any) => ({
          ...team,
          createdAt: new Date(team.createdAt)
        }));
      } catch (error) {
        console.error('Error parsing saved teams:', error);
        return [];
      }
    }
    return [];
  });
  const [favoriteTemplates, setFavoriteTemplates] = useState<string[]>(() => {
    const saved = localStorage.getItem('cricketFavoriteTemplates');
    return saved ? JSON.parse(saved) : [];
  });

  // Navigation state
  const [currentScreen, setCurrentScreen] = useState<'home' | 'games' | 'teams'>('home');
  const [showGamesScreen, setShowGamesScreen] = useState(false);
  const [showTeamsPlayersScreen, setShowTeamsPlayersScreen] = useState(false);

  const [editDialog, setEditDialog] = useState<EditDialog>({
    isOpen: false,
    playerType: "",
    currentName: "",
  });

  const [editTeamDialog, setEditTeamDialog] = useState<EditTeamDialog>({
    isOpen: false,
    teamLetter: "",
    currentName: "",
  });

  const [showSettings, setShowSettings] = useState(false);
  const [showWicketRecording, setShowWicketRecording] = useState(false);
  const [showScorecard, setShowScorecard] = useState(false);
  const [pendingRetirement, setPendingRetirement] = useState<PendingRetirement | null>(null);
  const [pendingMaxBallsOver, setPendingMaxBallsOver] = useState<PendingMaxBallsOver | null>(null);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < gameHistory.length - 1;

  const saveGameState = (newState: GameState) => {
    const newHistory = gameHistory.slice(0, historyIndex + 1);
    newHistory.push(newState);
    setGameHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setGameState(newState);
  };

  const getCurrentInnings = () => {
    return gameState.currentInnings === 1 ? gameState.innings1 : gameState.innings2!;
  };

  const getBattingTeamPlayers = () => {
    return gameState.battingTeam === 'A' ? gameState.teamAPlayers : gameState.teamBPlayers;
  };

  const getBowlingTeamPlayers = () => {
    return gameState.bowlingTeam === 'A' ? gameState.teamAPlayers : gameState.teamBPlayers;
  };

  const getBattingTeamName = () => {
    return gameState.battingTeam === 'A' ? gameState.teamA : gameState.teamB;
  };

  // Check for retirement after scoring
  const checkForRetirement = (stateToCheck?: GameState) => {
    const checkState = stateToCheck || gameState;
    if (!checkState.matchSettings.customRules.enableRetirement) return;
    
    const retirementRuns = checkState.matchSettings.customRules.retirementRuns;
    const currentInnings = checkState.currentInnings === 1 ? checkState.innings1 : checkState.innings2!;
    
    if (pendingRetirement) return;
    
    if (currentInnings.batter1.runs >= retirementRuns && !currentInnings.batter1.isRetired) {
      setPendingRetirement({ player: 'batter1', runs: currentInnings.batter1.runs });
      return;
    }
    
    if (currentInnings.batter2.runs >= retirementRuns && !currentInnings.batter2.isRetired) {
      setPendingRetirement({ player: 'batter2', runs: currentInnings.batter2.runs });
      return;
    }
  };

  // Check for max balls per over rule
  const checkForMaxBallsOverWithState = (state: GameState) => {
    if (!state.matchSettings.customRules.enableMaxBallsPerOver) return false;
    
    const currentInnings = state.currentInnings === 1 ? state.innings1 : state.innings2!;
    const maxBalls = state.matchSettings.customRules.maxBallsPerOver;
    const totalBallsInOver = currentInnings.currentOverBalls.length;
    const lastOverExemption = state.matchSettings.customRules.lastOverExemption;
    const isCurrentLastOver = currentInnings.currentOver === state.matchSettings.oversPerInnings;
    
    if (isCurrentLastOver && lastOverExemption) {
      return false;
    }
    
    if (pendingMaxBallsOver) return false;
    
    if (totalBallsInOver >= maxBalls) {
      setPendingMaxBallsOver({
        overNumber: currentInnings.currentOver,
        ballsInOver: totalBallsInOver,
        maxBalls: maxBalls,
        isLastOver: isCurrentLastOver && lastOverExemption
      });
      return true;
    }
    
    return false;
  };

  const handleRetirement = () => {
    if (!pendingRetirement) return;
    
    const newState = { ...gameState };
    const currentInnings = gameState.currentInnings === 1 ? newState.innings1 : newState.innings2!;
    const currentBatters: [string, string] = [currentInnings.batter1.name, currentInnings.batter2.name];
    
    if (pendingRetirement.player === 'batter1') {
      currentInnings.batter1.isRetired = true;
      currentInnings.batter1.onStrike = false;
      
      if (checkLastManStands(currentInnings, gameState)) {
        currentInnings.isLastManStands = true;
        currentInnings.batter1 = {
          name: "Last Man Stands",
          runs: 0,
          balls: 0,
          onStrike: false,
          isRetired: false,
        };
        currentInnings.batter2.onStrike = true;
      } else {
        const newBatter = {
          name: getNextBatterName(currentBatters, currentInnings, getBattingTeamPlayers()),
          runs: 0,
          balls: 0,
          onStrike: false,
          isRetired: false,
        };
        currentInnings.batter1 = newBatter;
        currentInnings.batter2.onStrike = true;
      }
    } else {
      currentInnings.batter2.isRetired = true;
      currentInnings.batter2.onStrike = false;
      
      if (checkLastManStands(currentInnings, gameState)) {
        currentInnings.isLastManStands = true;
        currentInnings.batter2 = {
          name: "Last Man Stands",
          runs: 0,
          balls: 0,
          onStrike: false,
          isRetired: false,
        };
        currentInnings.batter1.onStrike = true;
      } else {
        const newBatter = {
          name: getNextBatterName(currentBatters, currentInnings, getBattingTeamPlayers()),
          runs: 0,
          balls: 0,
          onStrike: false,
          isRetired: false,
        };
        currentInnings.batter2 = newBatter;
        currentInnings.batter1.onStrike = true;
      }
    }
    
    saveGameState(newState);
    setPendingRetirement(null);
  };

  const handleMaxBallsOver = () => {
    if (!pendingMaxBallsOver) return;
    
    const newState = { ...gameState };
    const currentInnings = gameState.currentInnings === 1 ? newState.innings1 : newState.innings2!;
    
    if (currentInnings.currentOverBalls.length > 0) {
      currentInnings.previousOvers.push([...currentInnings.currentOverBalls]);
    }
    
    currentInnings.ballsThisOver = 0;
    currentInnings.currentOverBalls = [];
    currentInnings.currentOver += 1;
    
    currentInnings.batter1.onStrike = !currentInnings.batter1.onStrike;
    currentInnings.batter2.onStrike = !currentInnings.batter2.onStrike;
    
    currentInnings.bowler = {
      name: getNextBowlerName(currentInnings.bowler.name, getBowlingTeamPlayers()),
      runsConceded: 0,
      ballsBowled: 0,
      wicketsTaken: 0,
    };
    
    saveGameState(newState);
    setPendingMaxBallsOver(null);
  };

  const handleOverrideRetirement = () => {
    setPendingRetirement(null);
  };

  const handleOverrideMaxBallsOver = () => {
    setPendingMaxBallsOver(null);
  };

  const handleScore = (action: string, runs?: number) => {
    if (action === "wicket") {
      setShowWicketRecording(true);
      return;
    }

    if (action === "undo") {
      if (canUndo) {
        setHistoryIndex(historyIndex - 1);
        setGameState(gameHistory[historyIndex - 1]);
      }
      return;
    }

    if (action === "redo") {
      if (canRedo) {
        setHistoryIndex(historyIndex + 1);
        setGameState(gameHistory[historyIndex + 1]);
      }
      return;
    }

    const result = handleScoringAction(gameState, action, runs, getBowlingTeamPlayers());
    
    if (result.shouldStartSecondInnings) {
      const updatedState = startSecondInnings(result.newState);
      saveGameState(updatedState);
      return;
    }

    saveGameState(result.newState);

    // Check max balls per over notification first
    const maxBallsTriggered = checkForMaxBallsOverWithState(result.newState);
    
    if (!maxBallsTriggered) {
      setTimeout(() => checkForRetirement(result.newState), 50);
    }
  };

  const handleWicketConfirm = (wicketDetails: WicketDetails) => {
    const result = handleWicketAction(
      gameState,
      wicketDetails,
      getBattingTeamPlayers(),
      getBowlingTeamPlayers()
    );

    if (result.shouldStartSecondInnings) {
      const updatedState = startSecondInnings(result.newState);
      saveGameState(updatedState);
      setShowWicketRecording(false);
      return;
    }

    saveGameState(result.newState);
    setShowWicketRecording(false);

    // Check max balls per over notification first
    const maxBallsTriggered = checkForMaxBallsOverWithState(result.newState);
    
    if (!maxBallsTriggered) {
      setTimeout(() => checkForRetirement(result.newState), 50);
    }
  };

  const handlePlayerEdit = (playerType: string) => {
    const currentInnings = getCurrentInnings();
    let currentName = "";
    switch (playerType) {
      case "batter1":
        currentName = currentInnings.batter1.name;
        break;
      case "batter2":
        currentName = currentInnings.batter2.name;
        break;
      case "bowler":
        currentName = currentInnings.bowler.name;
        break;
    }

    setEditDialog({
      isOpen: true,
      playerType,
      currentName,
    });
  };

  const handlePlayerNameSave = (newName: string) => {
    const newState = { ...gameState };
    const currentInnings = gameState.currentInnings === 1 ? newState.innings1 : newState.innings2!;

    switch (editDialog.playerType) {
      case "batter1":
        currentInnings.batter1.name = newName;
        break;
      case "batter2":
        currentInnings.batter2.name = newName;
        break;
      case "bowler":
        currentInnings.bowler.name = newName;
        break;
    }

    saveGameState(newState);
  };

  const handleAddNewPlayer = (newName: string) => {
    const newState = { ...gameState };

    if (editDialog.playerType === "batter1" || editDialog.playerType === "batter2") {
      const teamPlayers = gameState.battingTeam === 'A' ? newState.teamAPlayers : newState.teamBPlayers;
      if (!teamPlayers.includes(newName)) {
        teamPlayers.push(newName);
      }
    } else if (editDialog.playerType === "bowler") {
      const teamPlayers = gameState.bowlingTeam === 'A' ? newState.teamAPlayers : newState.teamBPlayers;
      if (!teamPlayers.includes(newName)) {
        teamPlayers.push(newName);
      }
    }

    saveGameState(newState);
  };

  const handleTeamEdit = (team: "A" | "B") => {
    const currentName = team === "A" ? gameState.teamA : gameState.teamB;
    setEditTeamDialog({
      isOpen: true,
      teamLetter: team,
      currentName,
    });
  };

  const handleTeamNameSave = (newName: string) => {
    const newState = { ...gameState };

    if (editTeamDialog.teamLetter === "A") {
      newState.teamA = newName;
    } else {
      newState.teamB = newName;
    }

    saveGameState(newState);
  };

  const handlePlayerSwitch = () => {
    const newState = { ...gameState };
    const currentInnings = gameState.currentInnings === 1 ? newState.innings1 : newState.innings2!;
    
    if (currentInnings.isLastManStands) return;
    
    currentInnings.batter1.onStrike = !currentInnings.batter1.onStrike;
    currentInnings.batter2.onStrike = !currentInnings.batter2.onStrike;

    saveGameState(newState);
  };

  const handleSettingsSave = (newSettings: typeof gameState.matchSettings) => {
    const newState = { ...gameState };
    newState.matchSettings = newSettings;
    saveGameState(newState);
  };

  const handleUpdateTeamPlayers = (teamA: string[], teamB: string[]) => {
    const newState = { ...gameState };
    newState.teamAPlayers = teamA;
    newState.teamBPlayers = teamB;
    saveGameState(newState);
  };

  const handleSaveTemplate = (template: Omit<GameTemplate, 'id' | 'createdAt'>) => {
    const newTemplate: GameTemplate = {
      ...template,
      id: Date.now().toString(),
      createdAt: new Date(),
    };
    setGameTemplates(prev => [...prev, newTemplate]);
  };

  const handleEditTemplate = (templateId: string, updatedTemplate: Omit<GameTemplate, 'id' | 'createdAt'>) => {
    setGameTemplates(prev => prev.map(template => 
      template.id === templateId 
        ? { ...template, ...updatedTemplate }
        : template
    ));
  };

  const handleToggleFavorite = (templateId: string) => {
    const newFavorites = favoriteTemplates.includes(templateId)
      ? favoriteTemplates.filter(id => id !== templateId)
      : [...favoriteTemplates, templateId];
    
    setFavoriteTemplates(newFavorites);
    localStorage.setItem('cricketFavoriteTemplates', JSON.stringify(newFavorites));
  };

  const handleEndGame = () => {
    const currentInnings = getCurrentInnings();
    const gameRecord: GameHistory = {
      id: Date.now().toString(),
      teamA: gameState.teamA,
      teamB: gameState.teamB,
      finalScore: `${currentInnings.runs}/${currentInnings.wicketsDown}`,
      format: `${gameState.matchSettings.oversPerInnings} overs, ${gameState.matchSettings.numberOfPlayers} players`,
      date: new Date(),
      completed: true,
      winner: gameState.winner,
    };
    
    setGameHistoryRecords(prev => [gameRecord, ...prev]);
    handleStartNewGame();
  };

  const handleEndInnings = () => {
    if (gameState.currentInnings === 1) {
      // End first innings and start second
      const updatedState = startSecondInnings(gameState);
      saveGameState(updatedState);
    } else {
      // End second innings (game over)
      const newState = { ...gameState };
      newState.gameComplete = true;
      newState.winner = determineWinner(newState);
      saveGameState(newState);
    }
  };

  const handleStartNewGame = (template?: GameTemplate) => {
    const newGameState = createInitialGameState(template);
    setGameState(newGameState);
    setGameHistory([newGameState]);
    setHistoryIndex(0);
    setShowGamesScreen(false);
  };

  const handleNavigateToGames = () => {
    setCurrentScreen('games');
    setShowGamesScreen(true);
    setShowTeamsPlayersScreen(false);
  };

  const handleNavigateToTeams = () => {
    setCurrentScreen('teams');
    setShowTeamsPlayersScreen(true);
    setShowGamesScreen(false);
  };

  const handleNavigateToHome = () => {
    setCurrentScreen('home');
    setShowGamesScreen(false);
    setShowTeamsPlayersScreen(false);
  };

  const handleSaveTeam = (team: Omit<SavedTeam, 'id' | 'createdAt'>) => {
    const newTeam: SavedTeam = {
      ...team,
      id: Date.now().toString(),
      createdAt: new Date(),
    };
    const updatedTeams = [...savedTeams, newTeam];
    setSavedTeams(updatedTeams);
    localStorage.setItem('cricketSavedTeams', JSON.stringify(updatedTeams));
  };

  const handleDeleteTeam = (id: string) => {
    const updatedTeams = savedTeams.filter(team => team.id !== id);
    setSavedTeams(updatedTeams);
    localStorage.setItem('cricketSavedTeams', JSON.stringify(updatedTeams));
  };

  const handleUpdateTeam = (id: string, updatedTeam: Omit<SavedTeam, 'id' | 'createdAt'>) => {
    const updatedTeams = savedTeams.map(team => 
      team.id === id 
        ? { ...team, ...updatedTeam }
        : team
    );
    setSavedTeams(updatedTeams);
    localStorage.setItem('cricketSavedTeams', JSON.stringify(updatedTeams));
  };

  const getTeamPlayersForEdit = () => {
    if (editDialog.playerType === "batter1" || editDialog.playerType === "batter2") {
      return getBattingTeamPlayers();
    } else if (editDialog.playerType === "bowler") {
      return getBowlingTeamPlayers();
    }
    return [];
  };

  return {
    // State
    gameState,
    gameHistory,
    historyIndex,
    gameTemplates,
    gameHistoryRecords,
    savedTeams,
    favoriteTemplates,
    currentScreen,
    editDialog,
    editTeamDialog,
    showSettings,
    showWicketRecording,
    showGamesScreen,
    showScorecard,
    showTeamsPlayersScreen,
    pendingRetirement,
    pendingMaxBallsOver,
    canUndo,
    canRedo,

    // State setters
    setShowSettings,
    setShowWicketRecording,
    setShowGamesScreen,
    setShowScorecard,
    setShowTeamsPlayersScreen,
    setEditDialog,
    setEditTeamDialog,
    setPendingRetirement,
    setPendingMaxBallsOver,

    // Getters
    getCurrentInnings,
    getBattingTeamPlayers,
    getBowlingTeamPlayers,
    getBattingTeamName,
    getTeamPlayersForEdit,

    // Handlers
    handleScore,
    handleWicketConfirm,
    handlePlayerEdit,
    handlePlayerNameSave,
    handleAddNewPlayer,
    handleTeamEdit,
    handleTeamNameSave,
    handlePlayerSwitch,
    handleSettingsSave,
    handleUpdateTeamPlayers,
    handleSaveTemplate,
    handleEditTemplate,
    handleToggleFavorite,
    handleEndGame,
    handleEndInnings,
    handleStartNewGame,
    handleRetirement,
    handleMaxBallsOver,
    handleOverrideRetirement,
    handleOverrideMaxBallsOver,
    handleSaveTeam,
    handleDeleteTeam,
    handleUpdateTeam,
    handleNavigateToGames,
    handleNavigateToTeams,
    handleNavigateToHome,
    
    // Template functions
    setGameTemplates,
    setGameHistoryRecords,
  };
}